import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  calculateMonthlyCustomerMetrics,
  getProjectedCustomerCountAtDate,
} from "@burnchart/financial";
import { CHART_COLORS } from "@/constants/chart-colors";
import type { CustomerProjectionData } from "../../utils/chart/customer-projection.utils";
import { safeDate, getFarFutureDate } from "../../utils/date";

interface CustomerGrowthChartProps {
  customerProjectionData: CustomerProjectionData;
  visibleDateRange: {
    startDate: Date;
    endDate: Date;
  };
  height?: number;
}

interface MonthlyCustomerData {
  month: string;
  date: Date;
  total: number;
  newCustomers: number;
  churnedCustomers: number;
  netChange: number;
  subscriptionRevenue: number;
  byProjection: Record<string, number>;
}

const TIER_LINE_COLORS = [
  CHART_COLORS.focus,
  CHART_COLORS.revenue,
  CHART_COLORS.cashPositive,
  CHART_COLORS.warn,
  CHART_COLORS.asset,
  CHART_COLORS.milestone,
];

const CustomerGrowthChart: React.FC<CustomerGrowthChartProps> = ({
  customerProjectionData,
  visibleDateRange,
  height = 300,
}) => {
  if (
    !customerProjectionData?.projections ||
    customerProjectionData.projections.length === 0
  ) {
    return null;
  }

  const missingTier = customerProjectionData.projections.some(
    (projection) =>
      !customerProjectionData.subscriptionTiers.some(
        (tier) => tier.id === projection.subscriptionTierId,
      ),
  );

  const { chartData, capacityWarnings } = useMemo(() => {
    const monthlyData: MonthlyCustomerData[] = [];
    const warningSet = new Set<string>();

    const allStartDates = customerProjectionData.projections
      .map((p) => safeDate(p.startDate).getTime())
      .filter((t) => !Number.isNaN(t));

    const allEndDates = customerProjectionData.projections
      .map((p) =>
        p.endDate ? new Date(p.endDate).getTime() : getFarFutureDate().getTime(),
      )
      .filter((t) => !Number.isNaN(t));

    const earliestStart =
      allStartDates.length > 0
        ? new Date(Math.min(...allStartDates))
        : visibleDateRange.startDate;

    const latestEnd =
      allEndDates.length > 0
        ? new Date(
            Math.min(Math.max(...allEndDates), visibleDateRange.endDate.getTime()),
          )
        : visibleDateRange.endDate;

    const startDate = new Date(
      Math.max(earliestStart.getTime(), visibleDateRange.startDate.getTime()),
    );
    const endDate = new Date(
      Math.min(latestEnd.getTime(), visibleDateRange.endDate.getTime()),
    );

    const currentMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

    while (currentMonth <= endMonth) {
      const metrics = calculateMonthlyCustomerMetrics(
        currentMonth,
        customerProjectionData,
      );
      metrics.warnings?.forEach((w) => warningSet.add(w));

      const byProjection: Record<string, number> = {};
      customerProjectionData.projections.forEach((projection) => {
        const tier = customerProjectionData.subscriptionTiers.find(
          (t) => t.id === projection.subscriptionTierId,
        );
        const projectionKey = tier?.name ?? projection.name;
        byProjection[projectionKey] = getProjectedCustomerCountAtDate(
          projection,
          currentMonth,
        );
      });

      monthlyData.push({
        month: currentMonth.toLocaleDateString("en-US", {
          month: "short",
          year: "2-digit",
        }),
        date: new Date(currentMonth),
        total: metrics.totalCustomers,
        newCustomers: metrics.newCustomers,
        churnedCustomers: metrics.churnedCustomers,
        netChange: metrics.netCustomerChange,
        subscriptionRevenue: metrics.subscriptionRevenue,
        byProjection,
      });

      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }

    return { chartData: monthlyData, capacityWarnings: Array.from(warningSet) };
  }, [customerProjectionData, visibleDateRange]);

  const projectionKeys = useMemo(() => {
    const keys = new Set<string>();
    chartData.forEach((month) => {
      Object.keys(month.byProjection).forEach((key) => keys.add(key));
    });
    return Array.from(keys);
  }, [chartData]);

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ payload: MonthlyCustomerData }>;
    label?: string;
  }) => {
    if (!active || !payload?.length) {
      return null;
    }

    const data = payload[0].payload;

    return (
      <div className="rounded-lg bg-surface-elevated px-3 py-2 text-xs text-white shadow-glow">
        <p className="mb-2 font-semibold">{label}</p>
        <div className="space-y-1">
          <p className="text-brand">
            Total customers: {Math.round(data.total).toLocaleString()}
          </p>
          <p className="text-revenue-muted">
            MRR: ${Math.round(data.subscriptionRevenue).toLocaleString()}
          </p>
          <p className="text-brand">
            New: +{Math.round(data.newCustomers).toLocaleString()}
          </p>
          <p className="text-expense">
            Churned: -{Math.round(data.churnedCustomers).toLocaleString()}
          </p>
          <p
            className={`font-semibold ${data.netChange >= 0 ? "text-brand" : "text-expense"}`}
          >
            Net change: {data.netChange >= 0 ? "+" : ""}
            {Math.round(data.netChange).toLocaleString()}
          </p>
          {Object.keys(data.byProjection).length > 1 ? (
            <div className="mt-1 border-t border-white/15 pt-1">
              {Object.entries(data.byProjection).map(([name, count]) => (
                <p key={name} className="text-white/55 text-xs">
                  {name}: {Math.round(count).toLocaleString()}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="sh-panel overflow-hidden">
      <div className="border-b border-white/10 bg-surface-secondary/60 px-4 py-2">
        <h4 className="text-sm font-medium text-white/75">
          Customer growth over time ({chartData.length} months)
        </h4>
        <p className="mt-1 text-xs text-white/45">
          Acquisition, churn, and subscription revenue from your customer projections
        </p>
        {missingTier ? (
          <p className="mt-2 text-xs text-warn">
            One or more projections reference a missing revenue plan — link a
            plan to include them in revenue.
          </p>
        ) : null}
        {capacityWarnings.length > 0 ? (
          <div className="mt-2 space-y-1">
            {capacityWarnings.map((warning) => (
              <p key={warning} className="text-xs text-warn">
                {warning}
              </p>
            ))}
          </div>
        ) : null}
      </div>
      <div className="p-4">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: "11px" }} iconType="line" />
            <Line
              type="monotone"
              dataKey="total"
              stroke={CHART_COLORS.cashPositive}
              strokeWidth={3}
              dot={false}
              name="Total customers"
            />
            {projectionKeys.length > 1
              ? projectionKeys.map((key, index) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={`byProjection.${key}`}
                    stroke={TIER_LINE_COLORS[index % TIER_LINE_COLORS.length]}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name={key}
                  />
                ))
              : null}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CustomerGrowthChart;
